/**
 * Simple tabs functionality. Used on manufacturer channel detail and disease channel detail pages.
 *
 * @example
 *
 * <div class="vu-tabs__wrapper js_tabsWrap">
 *     <button class="vu-tabs__navigation-button is-previous js_tabsPrevButton">Previous</button>
 *     <button class="vu-tabs__navigation-button is-next js_tabsNextButton">Next</button>
 *     <ul class="vu-tabs__list js_tabsList">
 *         <li class="vu-tabs__tab">
 *             <a href="...">...</a>
 *         </li>
 *         <li class="vu-tabs__tab">
 *             <button class="js_subtabListToggle">Sublist toggle</button>
 *             <div class="vu-tabs__subtab-list js_subtabList">
 *                 <a href="...">...</a>
 *                 <a href="...">...</a>
 *             </div>
 *         </li>
 *     </ul>
 * </div>
 *
 * <script src="vu_tabs.js">
 *   const vuTabs = new VuTabs();
 *   vuTabs.initialize();
 * </script>
 */
export default class VuTabs {
    initialize() {
        this._tabsScroll();
        this._subtabsToggle();
    }

    _tabsScroll() {
        const tabsWrap = document.querySelector('.js_tabsWrap');

        if (!tabsWrap) return;
        const tabsList = document.querySelector('.js_tabsList');
        const prevButton = document.querySelector('.js_tabsPrevButton');
        const nextButton = document.querySelector('.js_tabsNextButton');
        tabsList.addEventListener('scroll', () => {
            if (tabsList.scrollLeft > 0) {
                tabsWrap.classList.add('scrolling-right');
            } else {
                tabsWrap.classList.remove('scrolling-right');
            }
        });

        const scrollTabsList = (direction) => {
            // using scrollThreshold to cover edge cases
            // where there is only a few pixels left to scroll,
            // but the last item is already visible

            const scrollThreshold = direction === 'prev' ? -10 : 10;
            let scrollAmount = tabsList.clientWidth * 0.7;

            // If scrolling to the previous item, make the scroll amount negative
            if (direction === 'prev') {
                scrollAmount = -scrollAmount;
            }

            scrollAmount += scrollThreshold;

            if (tabsList.scrollLeft + scrollAmount >= tabsList.scrollWidth - tabsList.clientWidth) {
                nextButton?.classList.add('is-hidden');
                tabsWrap.classList.add('scrolled-to-end');
            } else {
                nextButton.classList.remove('is-hidden');
                tabsWrap.classList.remove('scrolled-to-end');
            }

            if (direction === 'next') {
                prevButton?.classList.remove('is-hidden');
            } else if (tabsList.scrollLeft <= Math.abs(scrollAmount)) {
                prevButton?.classList.add('is-hidden');
            }

            tabsList.scrollBy({ left: scrollAmount, behavior: 'smooth' });
        };

        // Attach click event listeners to the buttons
        prevButton?.addEventListener('click', () => scrollTabsList('prev'));
        nextButton?.addEventListener('click', () => scrollTabsList('next'));

        // initially show the next button if needed
        if (tabsList.scrollLeft < tabsList.scrollWidth - tabsList.clientWidth) {
            nextButton?.classList.remove('is-hidden');
        }
    }

    _subtabsToggle() {
        const subtabToggleButtons = Array.from(document.querySelectorAll('.js_subtabListToggle'));

        subtabToggleButtons.forEach((button) => {
            button.addEventListener('click', () => {
                const dropdown = button.nextElementSibling;
                const tabRect = button.getBoundingClientRect();
                const leftSpace = tabRect.left;
                const rightSpace = window.innerWidth - tabRect.right;

                if (leftSpace > rightSpace) {
                    dropdown.classList.add('align-right');
                    dropdown.classList.remove('align-left');
                } else {
                    dropdown.classList.add('align-left');
                    dropdown.classList.remove('align-right');
                }

                const currentActiveButton = document.querySelector('.js_subtabListToggle.is-active');
                currentActiveButton?.classList.remove('is-active');

                if (currentActiveButton !== button) {
                    button.classList.add('is-active');
                }
            });
        });
    }
}

window.addEventListener('DOMContentLoaded', () => {
    const vuTabs = new VuTabs();
    vuTabs.initialize();
});
